import { CompanyCensusExperience } from "@/components/company-census/CompanyCensusExperience";
import { OAuthBambooHRCallbackPage } from "@/components/company-census/OAuthBambooHRCallback";
import { OAuthQuickBooksCallbackPage } from "@/components/company-census/OAuthQuickBooksCallback";
import { loadCompanyCensusEnv } from "@/env";
import { AnalyticsProvider, RouteTracker } from "@cakewalk/frontend-api";
import { ErrorBoundary, ThemeProvider, Toaster } from "@cakewalk/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

/**
 * Top-level company-census SPA shell.
 *
 * The flow is the whole app: one URL per step (`/login`, `/business`, `/team`,
 * `/dependents`, `/complete`) so the browser back button works. Everything
 * lives behind the platform error boundary and the shared `@cakewalk/ui`
 * theme. TanStack Query is the only server-state manager
 * (`cross-cutting/data-fetching.mdc`).
 *
 * Analytics: one `<AnalyticsProvider>` at the root initialises PostHog (+ GTM)
 * and listens for the `cakewalk:*` events the shared packages dispatch (api
 * errors, error-boundary catches). `<RouteTracker />` fires a `$pageview` on
 * every step change. Empty `VITE_POSTHOG_KEY` or `MODE=test` makes it a no-op.
 * `<AnalyticsIdentity />` lives inside the flow where the auth state is known.
 */
export function App() {
  const env = loadCompanyCensusEnv();
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60_000,
            retry: (failureCount, error) => {
              const status = (error as { status?: number } | undefined)?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
          },
          mutations: { retry: false },
        },
      }),
    [],
  );
  const analyticsConfig = useMemo(
    () => ({
      platform: "census" as const,
      posthogKey: env.VITE_POSTHOG_KEY || undefined,
      posthogHost: env.VITE_POSTHOG_HOST,
      gtmId: env.VITE_GTM_ID || undefined,
      // Record every census session (see cross-cutting/analytics.mdc). The
      // shared adapter masks all inputs + `[data-sensitive]` text, so DOB / ZIP
      // / SSN / comp / addresses never enter a replay.
      sessionRecording: true,
      disabled: env.MODE === "test" || !env.VITE_POSTHOG_KEY,
    }),
    [env],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AnalyticsProvider config={analyticsConfig}>
          <ThemeProvider>
            <BrowserRouter>
              <RouteTracker />
              <Routes>
                <Route path="/" element={<Navigate to="/login" replace />} />
                {/* HR-connect OAuth popups land here (outside the step machine). */}
                <Route
                  path="/oauth/quickbooks/callback"
                  element={<OAuthQuickBooksCallbackPage />}
                />
                <Route path="/oauth/bamboohr/callback" element={<OAuthBambooHRCallbackPage />} />
                <Route path="/:stepSlug" element={<CompanyCensusExperience />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
              <Toaster />
            </BrowserRouter>
          </ThemeProvider>
        </AnalyticsProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
